import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import IconPicker from "components/admin/IconPicker";
import { useFetch } from "components/admin/hooks";
import api from "api/client";

/** Nhãn dễ hiểu cho các khóa trong config.texts. Khóa lạ thì hiện nguyên tên khóa. */
const TEXT_LABELS = {
  npc_chat: ["Lời NPC khi sự kiện đang chạy", true],
  npc_chat_view: ["Lời NPC sau khi hết giờ (giai đoạn xem)", true],
  guide: ["Nội dung nút Hướng dẫn", true],
  opt_guide: ["Tên nút Hướng dẫn"],
  opt_claim: ["Tên nút Nhận mốc quà"],
  opt_shop: ["Tên nút Shop đổi quà"],
  opt_checkin: ["Tên nút Điểm danh"],
  opt_goal: ["Tên nút Mốc toàn server"],
  opt_quest: ["Tên nút Nhiệm vụ"],
  opt_gift: ["Tên nút Tặng quà"],
  craft_ok: ["Thông báo khi chế thành công"],
  use_ok: ["Thông báo khi dùng vật phẩm"],
  banner_start: ["Banner lúc sự kiện bắt đầu"],
  banner_end_soon: ["Banner 1 giờ trước khi kết thúc"],
  banner_boss: ["Banner khi boss event xuất hiện"],
};

function textLabel(key) {
  if (TEXT_LABELS[key]) return TEXT_LABELS[key];
  if (key.startsWith("coin_")) return [`Tên tiền đổi quà "${key}"`];
  if (key.startsWith("opt_")) return [`Tên nút "${key.slice(4)}"`];
  return [key];
}

const TEXT_ORDER = Object.keys(TEXT_LABELS);

function sortTextKeys(keys) {
  const rank = (key) => {
    const i = TEXT_ORDER.indexOf(key);
    return i < 0 ? TEXT_ORDER.length : i;
  };
  return [...keys].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

/** NPC, chữ trên menu / banner và tên bảng top. Sửa được cả khi mùa đang chạy (trừ mã và map NPC). */
function AppearanceEditor({ config, started, update }) {
  const maps = useFetch(() => api.get("/api/admin/templates/maps"), []);
  const npc = config.npc || {};
  const texts = config.texts || {};
  const boards = config.boards || [];

  const setNpc = (patch) =>
    update((next) => {
      next.npc = { ...(next.npc || {}), ...patch };
    });
  const setText = (key, value) =>
    update((next) => {
      next.texts = { ...(next.texts || {}), [key]: value };
    });
  const setBoard = (index, patch) =>
    update((next) => {
      next.boards = (next.boards || []).map((b, i) => (i === index ? { ...b, ...patch } : b));
    });

  return (
    <MDBox display="flex" flexDirection="column" gap={2}>
      <MDTypography variant="h6">NPC sự kiện</MDTypography>
      {npc.id ? (
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <IconPicker
              folder="npcs"
              label="Ảnh NPC"
              value={npc.img || ""}
              onChange={(img) => setNpc({ img })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <MDInput
              fullWidth
              label="Tên NPC"
              value={npc.name || ""}
              onChange={(e) => setNpc({ name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <MDInput
              select
              fullWidth
              label="Đứng ở map"
              value={npc.map ?? ""}
              disabled={started}
              helperText={started ? "Mùa đã bắt đầu, không đổi map" : null}
              onChange={(e) => setNpc({ map: Number(e.target.value) })}
            >
              {(maps.data || []).map((map) => (
                <MenuItem key={map.mapId} value={map.mapId}>
                  {map.name} (#{map.mapId})
                </MenuItem>
              ))}
              {npc.map && !(maps.data || []).some((m) => m.mapId === npc.map) && (
                <MenuItem value={npc.map}>Map #{npc.map}</MenuItem>
              )}
            </MDInput>
          </Grid>
        </Grid>
      ) : (
        <Alert severity="info">Loại event này không có NPC riêng.</Alert>
      )}
      {started && (
        <Alert severity="info">
          Đổi ảnh NPC giữa mùa: người đang online thấy ảnh mới sau khi đăng nhập lại.
        </Alert>
      )}

      <Divider />
      <MDTypography variant="h6">Chữ hiển thị</MDTypography>
      <MDTypography variant="caption" color="text">
        {"{name}"} được thay bằng tên mùa. Để trống thì dùng chữ mặc định của loại event.
      </MDTypography>
      <Grid container spacing={2}>
        {sortTextKeys(Object.keys(texts)).map((key) => {
          const [label, long] = textLabel(key);
          return (
            <Grid item xs={12} md={long ? 12 : 6} key={key}>
              <MDInput
                fullWidth
                multiline={Boolean(long)}
                minRows={long ? 2 : undefined}
                label={label}
                value={texts[key] ?? ""}
                onChange={(e) => setText(key, e.target.value)}
              />
            </Grid>
          );
        })}
      </Grid>

      {boards.length > 0 && (
        <>
          <Divider />
          <MDTypography variant="h6">Bảng xếp hạng</MDTypography>
          <MDTypography variant="caption" color="text">
            Mẫu dòng: {"{rank}"} là hạng, {"{value}"} là điểm.
          </MDTypography>
          {boards.map((board, index) => (
            <Grid container spacing={2} key={board.key}>
              <Grid item xs={12} md={5}>
                <MDInput
                  fullWidth
                  label={`Tên bảng (${board.key})${board.enabled === false ? " · đang ẩn" : ""}`}
                  value={board.name || ""}
                  onChange={(e) => setBoard(index, { name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={7}>
                <MDInput
                  fullWidth
                  label="Mẫu dòng"
                  value={board.row || ""}
                  onChange={(e) => setBoard(index, { row: e.target.value })}
                />
              </Grid>
            </Grid>
          ))}
        </>
      )}
    </MDBox>
  );
}

AppearanceEditor.propTypes = {
  config: PropTypes.shape({
    npc: PropTypes.shape({}),
    texts: PropTypes.shape({}),
    boards: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  started: PropTypes.bool.isRequired,
  update: PropTypes.func.isRequired,
};

export default AppearanceEditor;
